import React from 'react';

import { Box } from '@chakra-ui/react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  type OnDragEndResponder,
} from '@hello-pangea/dnd';
import { type UseFieldArrayReturn } from 'react-hook-form';
import { LuGripVertical } from 'react-icons/lu';

import { InterviewQuestionCard } from '../admin-new-interview/InterviewCreateQuestionCard';
import { type NewInterviewFormInputs } from '../admin-new-interview/common';
import { type EditInterviewQuestionsFormInputs } from './schemas';

type DraggableQuestionListProps = {
  formArray:
    | UseFieldArrayReturn<NewInterviewFormInputs, 'questions', 'id'>
    | UseFieldArrayReturn<EditInterviewQuestionsFormInputs, 'questions', 'id'>;
  isEditMode?: boolean;
  onReorder?: () => void;
};

export const DraggableQuestionList: React.FC<DraggableQuestionListProps> = ({
  formArray,
  isEditMode = false,
  onReorder,
}) => {
  const { fields, move } = formArray;

  const handleDragEnd: OnDragEndResponder = (result) => {
    if (!result.destination) {
      return;
    }

    const startIndex = result.source.index;
    const endIndex = result.destination.index;

    if (startIndex === endIndex) {
      return;
    }

    // Move the item in the form array
    move(startIndex, endIndex);

    // Call the optional onReorder callback
    if (onReorder) {
      onReorder();
    }
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="questions-list">
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            bg={snapshot.isDraggingOver ? 'gray.50' : 'transparent'}
            borderRadius="md"
            transition="background-color 0.2s"
          >
            {fields.map((field, index) => (
              <Draggable
                key={field.id}
                draggableId={field.id}
                index={index}
                isDragDisabled={!isEditMode}
              >
                {(provided, snapshot) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    mb={4}
                    opacity={snapshot.isDragging ? 0.8 : 1}
                    transition="opacity 0.2s ease"
                    boxShadow={
                      snapshot.isDragging
                        ? '0 8px 25px rgba(0, 0, 0, 0.15)'
                        : 'none'
                    }
                  >
                    <Box position="relative">
                      {isEditMode && (
                        <Box
                          {...provided.dragHandleProps}
                          position="absolute"
                          left="-50px"
                          top="50%"
                          transform="translateY(-50%)"
                          cursor="grab"
                          p={2}
                          borderRadius="md"
                          _hover={{ bg: 'gray.100' }}
                          _active={{ cursor: 'grabbing' }}
                          zIndex={10}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          bg="white"
                          border="1px solid"
                          borderColor="gray.200"
                          boxShadow="sm"
                        >
                          <LuGripVertical color="gray.500" />
                        </Box>
                      )}

                      <InterviewQuestionCard
                        index={index}
                        field={field}
                        fieldsLength={fields.length}
                        remove={formArray.remove}
                        isEditMode={isEditMode}
                      />
                    </Box>
                  </Box>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    </DragDropContext>
  );
};
